import { useState, type ReactNode } from 'react'
import { PageHeader } from '~/components/PageHeader'
import { cn } from '~/lib/utils'

// 서비스 페이지 공통 셸: 제목 + 라인 언더라인 탭 + 탭 콘텐츠.
// 활성 탭은 클릭으로 전환되고, 비활성(향후) 탭은 disabled로 흐리게 노출한다.
const FUTURE_TABS = ['에이전트'] as const

export interface ServiceTab {
    label: string
    content: ReactNode
}

export function ServicePageShell({
    title,
    description,
    tabs,
    active: activeProp,
    onActiveChange,
}: {
    title: string
    description?: string
    tabs: ServiceTab[]
    /** 제어형 활성 탭 인덱스. 지정 시 onActiveChange와 함께 부모가 탭을 제어한다. */
    active?: number
    onActiveChange?: (index: number) => void
}) {
    const [activeState, setActiveState] = useState(0)
    const active = activeProp ?? activeState
    const setActive = (index: number) => {
        setActiveState(index)
        onActiveChange?.(index)
    }

    return (
        <div>
            <PageHeader title={title} description={description} />
            <div
                role='tablist'
                aria-label={`${title} 탭`}
                className='border-border flex items-center gap-4 border-b'>
                {tabs.map((tab, i) => {
                    const a = i === active
                    return (
                        <button
                            key={tab.label}
                            type='button'
                            role='tab'
                            aria-selected={a}
                            onClick={() => setActive(i)}
                            className={cn(
                                'h-9 border-b-2 px-1 text-[13px] font-medium transition-colors',
                                a
                                    ? 'text-foreground border-primary'
                                    : 'text-muted-foreground hover:text-foreground border-transparent'
                            )}>
                            {tab.label}
                        </button>
                    )
                })}
                {FUTURE_TABS.map((label) => (
                    <button
                        key={label}
                        type='button'
                        role='tab'
                        aria-selected={false}
                        aria-disabled='true'
                        disabled
                        className='text-muted-foreground/50 h-9 cursor-not-allowed border-b-2 border-transparent px-1 text-[13px] font-medium'>
                        {label}
                    </button>
                ))}
            </div>
            <section role='tabpanel' className='mt-4'>
                {tabs[active]?.content}
            </section>
        </div>
    )
}
